import { escapeCsvCell } from '@/lib/utils/export-xlsx'
import type { PopulationFilterValue } from '@/lib/population/types'
import { FACTOR_GROUP_KEY, HIDDEN_REASON_KEY, LEVEL_KEY } from './labels'
import type { DimensionId, FactorGroup, VcroBreakdown, VcroBreakdownRow } from './types'

/**
 * The organisation screens' pure logic (vCRO wave 2, docs/product/modules/
 * vcro/2026-10-03-wave-2-where-the-risk-sits.md).
 *
 * Nothing here scores or bands. Every score, level, rate and cell arrives from
 * the API (the one risk engine); these functions only say where a figure goes:
 * which request, which People link, which side of the engine's own neutral
 * point, which colour on the heatmap's points scale, which CSV cell, and how
 * an SVG label stays inside its box in either direction.
 */

/** The eight factor groups, in the contract's (and the heatmap's) order. */
export const FACTOR_GROUPS = [
  'simulations',
  'reporting',
  'realReports',
  'phishTrainer',
  'coaching',
  'training',
  'quiz',
  'attitude',
] as const satisfies readonly FactorGroup[]

/**
 * The engine's neutral score (RISK_NEUTRAL_SCORE): everyone starts at 50,
 * evidence moves them. On the vCRO 0..100 parts a value below it is evidence
 * that lowers risk and above it evidence that raises it. (A heatmap cell is
 * not on that scale: it is signed points, neutral at 0. See heatCellTone.)
 */
export const RISK_NEUTRAL = 50

// ── Dimensions ──────────────────────────────────────────────────────────────

export const DEFAULT_DIMENSION: DimensionId = 'department'
/** URL param holding the chosen dimension. */
export const DIMENSION_PARAM = 'dimension'

const BUILT_INS = ['department', 'location', 'designation'] as const

export function isDimensionId(value: string): value is DimensionId {
  if ((BUILT_INS as readonly string[]).includes(value)) return true
  return /^(category|custom):.+$/.test(value)
}

export function readDimension(sp: { get(name: string): string | null }): DimensionId {
  const raw = sp.get(DIMENSION_PARAM)?.trim() ?? ''
  return isDimensionId(raw) ? raw : DEFAULT_DIMENSION
}

/** A copy of `sp` with the dimension set (left out at its default). */
export function writeDimension(sp: URLSearchParams, dimension: DimensionId): URLSearchParams {
  const next = new URLSearchParams(sp.toString())
  next.delete(DIMENSION_PARAM)
  if (dimension !== DEFAULT_DIMENSION) next.set(DIMENSION_PARAM, dimension)
  return next
}

/** GET /vcro/breakdown's query: the dimension and the population filter, arrays repeated. */
export function breakdownRequestQuery(dimension: DimensionId, population: PopulationFilterValue): string {
  const out = new URLSearchParams()
  out.set('dimension', dimension)
  for (const id of population.groupIds) out.append('groupIds', id)
  for (const id of population.categoryIds) out.append('categoryIds', id)
  for (const id of population.tagIds) out.append('tagIds', id)
  for (const entry of population.cf) out.append('cf', entry)
  return out.toString()
}

/**
 * Where a bucket opens: the People list filtered to it, or null where People
 * has no such filter (a link that showed everyone would mislead). A
 * department, location or designation by its value (GET /vcro/people drops a
 * blank one as "no filter", so their "not set" bucket has no link), a
 * category's bucket by its group id (a category's "in no group" has no
 * filter), a custom field's by the wave 0 `key:value` entry (`key:` = not
 * set, which the population filter does honour).
 */
export function peopleHrefForBucket(dimension: DimensionId, bucket: string): string | null {
  const qs = new URLSearchParams()
  if (dimension === 'department') {
    if (!bucket.trim()) return null
    qs.set('department', bucket)
  } else if (dimension === 'location' || dimension === 'designation') {
    if (!bucket.trim()) return null
    qs.set(dimension, bucket)
  } else if (dimension.startsWith('category:')) {
    if (!bucket) return null
    qs.set('groupIds', bucket)
  } else {
    qs.set('cf', `${dimension.slice('custom:'.length)}:${bucket}`)
  }
  return `/vcro/people?${qs.toString()}`
}

/** The bucket id of the total row (W2-API): the organisation, on an exclusive dimension only. */
export const TOTAL_BUCKET = '*'

// ── The heatmap's scale: signed points ─────────────────────────────────────

export type HeatTone = 'lowers' | 'neutral' | 'raises'

/**
 * Which side of 0 a cell sits on. A cell is the average measured person's
 * signed points from a group of factors (W2-API polish, 2026-10-03): above 0
 * the group raises the score, below 0 it lowers it. What prints as "0.0" is
 * neutral.
 */
export function heatCellTone(points: number): HeatTone {
  if (Math.abs(points) < 0.05) return 'neutral'
  return points < 0 ? 'lowers' : 'raises'
}

/** At the grid's largest absolute value a cell is this share of its pole; the rest is the neutral surface. */
const POLE_MAX_PERCENT = 62

/**
 * The largest absolute value among the figures shown: the heatmap's colour
 * stretches to it, so the strongest cell on screen is always the full pole
 * (on real data every group moves the score by a few points, and a fixed
 * scale painted them all the same pale shade). 0 when there is no figure.
 */
export function heatScaleMax(rows: readonly (readonly (number | null)[])[]): number {
  let max = 0
  for (const row of rows) for (const v of row) if (v !== null && Math.abs(v) > max) max = Math.abs(v)
  return max
}

/**
 * A diverging colour on the theme's own tokens, centred on 0: the neutral
 * surface at 0, mixing toward the "lowers" pole below it and the "raises"
 * pole above, in proportion to the cell's share of `max` (heatScaleMax).
 * Continuous: it never bands a value into a level. The page defines the
 * three `--vcro-heat-*` variables (VCRO_LEVEL_TOKENS).
 */
export function heatCellBackground(points: number, max: number): string {
  const tone = heatCellTone(points)
  const strength = tone === 'neutral' || max <= 0 ? 0 : Math.min(1, Math.abs(points) / max)
  const percent = Math.round(strength * POLE_MAX_PERCENT)
  const pole = tone === 'lowers' ? 'var(--vcro-heat-lowers)' : 'var(--vcro-heat-raises)'
  return `color-mix(in oklab, ${pole} ${percent}%, var(--vcro-heat-neutral))`
}

/** The legend's gradient, from minus the largest absolute value at the inline start to plus it. */
export function heatLegendBackground(dir: 'ltr' | 'rtl'): string {
  const to = dir === 'rtl' ? 'to left' : 'to right'
  return `linear-gradient(${to}, ${heatCellBackground(-1, 1)}, ${heatCellBackground(0, 1)}, ${heatCellBackground(1, 1)})`
}

// ── SVG labels that stay inside their box ───────────────────────────────────

/** Hebrew, Arabic, Syriac, Thaana, N'Ko and their presentation forms. */
const RTL_LETTER = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/
const LTR_LETTER = /\p{L}/u

/**
 * A label's own direction, from its first strong letter (what `dir="auto"`
 * does for HTML): an English group name in an Arabic screen is still read
 * left to right, so its ellipsis belongs on its right. No letter: ltr.
 */
export function textDirection(text: string): 'ltr' | 'rtl' {
  for (const ch of text) {
    if (RTL_LETTER.test(ch)) return 'rtl'
    if (LTR_LETTER.test(ch)) return 'ltr'
  }
  return 'ltr'
}

/**
 * The SVG `text-anchor` that makes text pinned to a box's left or right edge
 * run INTO the box. `text-anchor` is relative to the text's own direction:
 * "start" is the right end of right-to-left text, so an Arabic label pinned
 * to a right edge starts there, and an English one ends there.
 */
export function svgAnchor(edge: 'left' | 'right', dir: 'ltr' | 'rtl'): 'start' | 'end' {
  return (edge === 'left') === (dir === 'ltr') ? 'start' : 'end'
}

const ELLIPSIS = '\u2026'

/** `text` in at most `width` characters, an ellipsis at its own end when cut. */
function fit(text: string, width: number): string {
  return text.length > width ? `${text.slice(0, Math.max(1, width - 1))}${ELLIPSIS}` : text
}

/**
 * A label on at most `lines` lines of at most `width` characters, broken at
 * spaces. Only what still does not fit is cut, at the end of the last line;
 * a word longer than a line is cut at the end of its own line.
 */
export function wrapLabel(text: string, width: number, lines: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean)
  if (!words.length) return ['']
  const out: string[] = []
  let i = 0
  while (i < words.length && out.length < lines) {
    if (out.length === lines - 1) {
      out.push(fit(words.slice(i).join(' '), width))
      return out
    }
    let line = words[i]
    i++
    if (line.length > width) {
      out.push(fit(line, width))
      continue
    }
    while (i < words.length && line.length + 1 + words[i].length <= width) {
      line = `${line} ${words[i]}`
      i++
    }
    out.push(line)
  }
  return out
}

// ── Knowledge against behaviour ─────────────────────────────────────────────

export type Quadrant = 'actNow' | 'slips' | 'teach' | 'safe'

/**
 * A bucket's quadrant from its two parts against the neutral 50. Above it the
 * evidence raises risk; exactly 50 is no evidence either way, so it counts on
 * the safer side.
 */
export function quadrantOf(knowledge: number, behaviour: number): Quadrant {
  const knowsLess = knowledge > RISK_NEUTRAL
  const slips = behaviour > RISK_NEUTRAL
  if (knowsLess && slips) return 'actNow'
  if (slips) return 'slips'
  if (knowsLess) return 'teach'
  return 'safe'
}

export const QUADRANTS: readonly Quadrant[] = ['actNow', 'slips', 'teach', 'safe']

// ── Change ──────────────────────────────────────────────────────────────────

/**
 * The score on the "since" day: the API's `change` is today's score less that
 * day's, so this is exact, not an estimate. Unknown when either is.
 */
export function scoreOnSince(score: number | null, change: number | null): number | null {
  if (score === null || change === null) return null
  return Math.round((score - change) * 10) / 10
}

// ── The scorecard ───────────────────────────────────────────────────────────

export type BreakdownSort = 'label' | 'people' | 'score' | 'change' | 'highCritical' | 'reportRate' | 'trainingComplete'
export type SortOrder = 'asc' | 'desc'

const sortValue = (row: VcroBreakdownRow, key: Exclude<BreakdownSort, 'label'>): number | null =>
  row.hidden && key !== 'people' ? null : row[key]

/**
 * The buckets in the chosen order. Every bucket is on the page (the API sends
 * them all), so this sorts the whole set. A bucket with no value for the
 * column sits last whichever the order, hidden ones after the rest.
 */
export function sortBreakdownRows(rows: readonly VcroBreakdownRow[], key: BreakdownSort, order: SortOrder): VcroBreakdownRow[] {
  const sign = order === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    if (a.hidden !== b.hidden) return a.hidden ? 1 : -1
    if (key === 'label') return sign * a.label.localeCompare(b.label)
    const va = sortValue(a, key)
    const vb = sortValue(b, key)
    if (va === null && vb === null) return a.label.localeCompare(b.label)
    if (va === null) return 1
    if (vb === null) return -1
    return sign * (va - vb) || a.label.localeCompare(b.label)
  })
}

type Translate = (key: string, params?: Record<string, string | number>) => string

const num = (v: number | null) => (v === null ? '' : String(v))

/**
 * The scorecard as CSV cells (escaped, a formula neutralised): a header, a row
 * per bucket, then the total for an exclusive dimension. A hidden bucket keeps
 * its counts and has every score and rate empty; the last column says why a
 * bucket has no score. Numbers are written plainly (a dot for decimals), so a
 * spreadsheet reads them as numbers.
 */
export function breakdownCsvRows(b: VcroBreakdown, t: Translate): string[][] {
  const header = [
    'bucket',
    'people',
    'measured',
    'score',
    'level',
    'change',
    'highCritical',
    'reportRate',
    'trainingComplete',
    'policyAcknowledged',
    'topFactorGroup',
    'behaviour',
    'knowledge',
    'state',
  ].map((k) => escapeCsvCell(t(`vcro.breakdown.columns.${k}`)))
  const line = (row: VcroBreakdownRow, label: string): string[] => [
    escapeCsvCell(label),
    String(row.people),
    String(row.measured),
    num(row.score),
    row.score === null ? '' : escapeCsvCell(t(LEVEL_KEY[row.level])),
    num(row.change),
    row.hidden ? '' : String(row.highCritical),
    num(row.reportRate),
    num(row.trainingComplete),
    num(row.policyAcknowledged),
    row.topFactorGroup ? escapeCsvCell(t(FACTOR_GROUP_KEY[row.topFactorGroup])) : '',
    num(row.behaviour),
    num(row.knowledge),
    row.hiddenReason ? escapeCsvCell(t(HIDDEN_REASON_KEY[row.hiddenReason].csv)) : '',
  ]
  const out = [header, ...b.rows.map((row) => line(row, row.label || t('vcro.breakdown.notSet')))]
  if (b.exclusive && b.total) out.push(line(b.total, t('vcro.breakdown.total')))
  return out
}
